import { ENDPOINTS, useActions } from 'hammock';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { ContentMetadata, ContentSegments } from '@candu-ai/content/types';
import { DocumentPreview, IFrameOverlay } from '@candu-ai/document/preview';
import { useAuthentication } from 'commons/hooks';
import { useEditorNode, useNodeContext, useSelection } from 'craft/hooks';

import { EmptyState } from './components/EmptyState';
import { FloatingHeader } from './components/FloatingHeader';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 40px;

  candu-component,
  .candu-component {
    pointer-events: none !important;
  }
`;

interface Props {
  slug?: string;
  contentList: ContentMetadata[];
}

export const ContentEmbed = ({ slug, contentList }: Props) => {
  const { id } = useNodeContext();
  const { setProps } = useEditorNode(id);
  const { applicationId }: any = useAuthentication();

  const selectedContent = contentList.find((content) => content.slug === slug);
  const value = selectedContent ? slug || '' : '';
  const { selection } = useSelection();

  const { get } = useActions(ENDPOINTS.contentSegments) as any;
  const [isDocumentPublished, setIsDocumentPublished] = useState(false);
  const [contentSegment, setContentSegment] = useState<ContentSegments | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<number | undefined>(
    selectedContent?.defaultDocumentId || undefined,
  );

  const setSlug = (newSlug: string) => {
    setProps({ slug: newSlug });
  };

  useEffect(() => {
    setContentSegment(null);

    if (selectedContent) {
      get({ id: selectedContent.id }).then((resource: any) => {
        setContentSegment(resource);
        if (!selectedContent.defaultDocumentId) {
          setIsDocumentPublished(
            resource?.segmentDocuments[0].lastModifiedAt ===
              resource?.segmentDocuments[0].lastPublishedAt,
          );
          setSelectedDocument(resource?.segmentDocuments[0].documentId);
        } else {
          setIsDocumentPublished(
            resource.defaultDocumentLastModifiedAt === resource.defaultDocumentLastPublishedAt,
          );
          setSelectedDocument(selectedContent.defaultDocumentId);
        }
      });
    }
  }, [selectedContent]);

  const onChangeDocument = (documentId: number) => {
    setSelectedDocument(documentId);

    if (documentId !== contentSegment?.defaultDocumentId) {
      const foundDocument = contentSegment?.segmentDocuments.find(
        (doc: any) => doc.documentId === documentId,
      );
      setIsDocumentPublished(foundDocument?.lastModifiedAt === foundDocument?.lastPublishedAt);
    } else {
      setIsDocumentPublished(
        contentSegment.defaultDocumentLastModifiedAt ===
          contentSegment.defaultDocumentLastPublishedAt,
      );
    }
  };

  if (!selectedContent) {
    return <EmptyState contentList={contentList} setSlug={setSlug} />;
  }

  const hasFocus = selection.all.length === 1 && selection.all[0] === id;

  return (
    <Container>
      {hasFocus && (
        <FloatingHeader
          slug={value}
          setSlug={setSlug}
          contentList={contentList}
          selectedContent={selectedContent}
          contentSegment={contentSegment}
          selectedDocument={selectedDocument}
          setSelectedDocument={onChangeDocument}
        />
      )}
      {!hasFocus && <IFrameOverlay />}
      {selectedDocument && (
        <DocumentPreview
          applicationId={applicationId}
          documentId={selectedDocument}
          stylesheetFromSource={!isDocumentPublished}
          checkeredBackdrop={false}
          isSdkPreview
        />
      )}
    </Container>
  );
};
